import React from 'react';
import './SearchableTable.scss';
import SearchableTableBar from 'components/reusables/SearchableTableBar/SearchableTableBar';
import { Pagination, Table } from 'antd';
import { TablePaginationConfig, TableProps } from 'antd/es/table';
import { JobStatus } from 'components/StatusTag/StatusTag';
import ChidoriIcon from 'assets/icons/chidori-icon.svg';
import clsx from 'clsx';
import dayjs from 'dayjs';
import SpinnerChidori from 'components/SpinnerChidori/SpinnerChidori';

export enum DATA_VIEW {
  TABLE_VIEW = 'TABLE_VIEW',
  CHART_VIEW = 'CHART_VIEW',
}

type SearchableTableProps<T> = {
  showAllJobs: boolean;
  isLoading: boolean;
  disableFilterSelectors?: boolean;
  PreContent?: JSX.Element;
  PostContent?: JSX.Element;
  jobIDFilter: string;
  setJobIDFilter: React.Dispatch<React.SetStateAction<string>>;
  jobStatusFilters: JobStatus[];
  setJobStatusFilters: React.Dispatch<React.SetStateAction<JobStatus[]>>;
  dateRangePicker: dayjs.Dayjs[] | null[];
  setDateRangePicker: React.Dispatch<
    React.SetStateAction<dayjs.Dayjs[] | null[]>
  >;
  tableFilter?: string[];
  setTableFilter?: React.Dispatch<React.SetStateAction<string[]>>;
  schemaFilters?: string[];
  setSchemaFilters?: React.Dispatch<React.SetStateAction<string[]>>;
  schemas?: string[];
  tables?: string[];
  Chart?: JSX.Element;
  rowSelection?: TableProps<T>['rowSelection'];
  loading: boolean;
  pagination: TablePaginationConfig;
  dataSourceTotalCount: number;
  addPostContent?: boolean;
} & TableProps<T>;

function SearchableTable<T extends object>({
  showAllJobs,
  PreContent,
  PostContent,
  disableFilterSelectors,
  jobIDFilter,
  setJobIDFilter,
  jobStatusFilters,
  setJobStatusFilters,
  dateRangePicker,
  setDateRangePicker,
  schemas,
  tables,
  tableFilter,
  setTableFilter,
  schemaFilters,
  setSchemaFilters,
  Chart,
  rowSelection,
  loading,
  pagination,
  dataSource,
  dataSourceTotalCount,
  columns,
  addPostContent,
  isLoading,
  ...restProps
}: SearchableTableProps<T>) {
  return (
    <section className="searchable-table__wrapper">
      <section
        className={clsx(
          'searchable-table__bar-container',
          showAllJobs && 'all-jobs'
        )}>
        {PreContent ?? null}
        <SearchableTableBar<T>
          disableFilterSelectors={disableFilterSelectors}
          showAllJobs={showAllJobs}
          addPostContent={addPostContent}
          dataSource={dataSource}
          dataSourceTotalCount={dataSourceTotalCount}
          jobIDFilter={jobIDFilter}
          setJobIDFilter={setJobIDFilter}
          dateRangePicker={dateRangePicker}
          setDateRangePicker={setDateRangePicker}
          tableFilter={tableFilter}
          setTableFilter={setTableFilter}
          schemaFilters={schemaFilters}
          setSchemaFilters={setSchemaFilters}
          schemas={schemas}
          tables={tables}
          postContent={PostContent}
        />
      </section>
      {isLoading ? (
        <div className="spinner-wrapper">
          <SpinnerChidori />
        </div>
      ) : (
        <>
          <section className="searchable-table__table-container">
            <Table<T>
              rowKey="sparkJobId"
              rowSelection={
                rowSelection ? { type: 'checkbox', ...rowSelection } : undefined
              }
              className="searchable-table__component"
              dataSource={dataSource}
              columns={columns}
              loading={{
                spinning: loading,
                indicator: (
                  <img
                    className="searchable-table__loading-spinner"
                    src={ChidoriIcon}
                    alt="Chidori Icon"
                  />
                ),
              }}
              pagination={false}
              scroll={{
                y: 'var(--searchable-table-scroll-y, calc(100vh - 280px))',
              }}
              {...restProps}
            />
          </section>

          <section className="searchable-table__pagination searchable-table__pagination--next-prev-only">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showSizeChanger={pagination.showSizeChanger}
              showTotal={pagination.showTotal}
              onChange={(page, pageSize) => {
                pagination.onChange?.(page, pageSize);
              }}
              onShowSizeChange={(current, size) => {
                pagination.onShowSizeChange?.(current, size);
              }}
            />
          </section>
        </>
      )}
    </section>
  );
}

export default SearchableTable;
